import { flexRender, Table as TanstackTable } from '@tanstack/react-table'
import { Loader2 } from 'lucide-react'
import { useRef } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/shared/components/ui/table'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { TableProduct } from '../types'

interface ProductsTableProps {
  table: TanstackTable<TableProduct>
  columns: any[]
  productsCount: number
  hasActiveSearch: boolean
  isLoading?: boolean
}

export const ProductsTable = ({
  table,
  columns,
  productsCount,
  hasActiveSearch,
  isLoading,
}: ProductsTableProps) => {
  const { t } = useLocalization()
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const rows = table.getRowModel().rows

  return (
    <div
      className="border rounded-lg bg-white"
      style={{
        height: 'calc(100vh - 300px)',
        overflowY: 'auto',
        position: 'relative',
      }}
      ref={tableContainerRef}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 z-20 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
        </div>
      )}
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-gray-50">
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className="h-12 px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center gap-1.5">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {productsCount === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24">
                <div className="w-fit mx-auto my-4 text-center">
                  <p className="text-muted-foreground">
                    {t('No results found', 'Inget resultat hittades')}
                  </p>
                </div>
              </TableCell>
            </TableRow>
          ) : rows.length > 0 ? (
            rows.map((row, index) => (
              <TableRow
                key={row.id}
                className={`border-b border-gray-100 hover:bg-[inherit] ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-50'
                }`}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="px-4 py-3 align-middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24">
                <p className="w-fit mx-auto my-4 text-muted-foreground">
                  {hasActiveSearch
                    ? t(
                        'No products found matching your search.',
                        'Inga produkter hittades som matchar din sökning.'
                      )
                    : t(
                        'You need to search for something dawg 🐶',
                        'Du behöver söka efter något 🐶'
                      )}
                </p>
                {hasActiveSearch && (
                  <p className="w-fit mx-auto text-xs text-muted-foreground">
                    {t(
                      'Try adjusting your search terms or clearing the search.',
                      'Prova att justera dina sökord eller rensa sökningen.'
                    )}
                  </p>
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
